<script setup lang="ts">
/**
 * 期限輸入：**數值＋單位下拉**（天／小時／分鐘）——批108。
 *
 * 期限的最小單位從「天」降到「分鐘」之後，所有收期限的表單都要能表達「30 分鐘」。
 * 對外的 v-model 是**總分鐘**（`term_days * 1440 + term_minutes`），呼叫端送出前用
 * `lib/terms.splitMinutes` 拆回兩欄即可。
 *
 * ★★ 批106 的坑：Vue 3 對 `<input type="number">` 會把 v-model **自動轉成數字**，
 *    所以綁定值絕不能再呼叫 `.trim()`（初值 `''` 是字串所以載入正常，**一打字就 TypeError**）。
 *    這裡一律走 `lib/numField` 的 `numOrNull`。
 */
import { computed, ref, watch } from 'vue'

import { numOrNull } from '@/lib/numField'
import { TERM_UNIT_LABELS, type TermUnit, fromUnitValue, toUnitValue } from '@/lib/terms'

const props = withDefaults(
  defineProps<{
    /** 總分鐘；`null`＝未指定（沿用預設／模板）。 */
    modelValue: number | null
    disabled?: boolean
    /** 允許清空（顯示為「未指定」）。 */
    nullable?: boolean
  }>(),
  { disabled: false, nullable: false },
)
const emit = defineEmits<{ 'update:modelValue': [number | null] }>()

const amount = ref<number | string | null>(null)
const unit = ref<TermUnit>('day')

/** 外部值 → 表單（挑能整除的最大單位，43200 → 30 天而不是 43200 分鐘）。 */
watch(
  () => props.modelValue,
  (total) => {
    if (total == null) {
      amount.value = null
      return
    }
    const [value, u] = toUnitValue(total)
    // 只有在換算結果與目前顯示不同時才覆寫，免得打字打到一半被跳回去。
    if (fromUnitValue(numOrNull(amount.value), unit.value) !== total) {
      amount.value = value
      unit.value = u
    }
  },
  { immediate: true },
)

function push() {
  const n = numOrNull(amount.value)
  if (n == null) {
    emit('update:modelValue', props.nullable ? null : 0)
    return
  }
  emit('update:modelValue', fromUnitValue(n, unit.value))
}

const units = computed(() => Object.entries(TERM_UNIT_LABELS) as [TermUnit, string][])
</script>

<template>
  <div class="term-field">
    <input
      v-model="amount"
      type="number"
      min="0"
      step="1"
      :disabled="disabled"
      :placeholder="nullable ? '未指定' : '0'"
      @input="push"
    />
    <select v-model="unit" :disabled="disabled" @change="push">
      <option v-for="[value, label] in units" :key="value" :value="value">{{ label }}</option>
    </select>
  </div>
</template>

<style scoped>
.term-field {
  display: flex;
  gap: 6px;
  align-items: center;
}
.term-field input {
  /* 數值欄不必太寬；單位下拉才是主角 */
  width: 7.5em;
  min-width: 0;
}
.term-field select {
  width: auto;
  min-width: 5.5em;
}
</style>
